/**
 * Die Warteblase des Schmieds im Verlauf. (16.10.2026, ersetzt die Pille „Schmied arbeitet: …“ über dem Eingabefeld)
 *
 * Martin: die Wartepille soll als WARTEBLASE im Chat stehen — dort, wo der Text des Schmieds gleich erscheint (am Ende des
 * Verlaufs), mit tippenden Pünktchen, in der Handschrift der Entwickler-Blase (Salbei, gestrichelte Kante, Name in Kapitälchen).
 *
 * Die Quelle bleibt dieselbe: das Ereignis `schmied-status` vom Chat-Strom (server/schmied-status.ts, shared/schmied-status.ts).
 * Kein Chat-Beitrag: kein `data-ts`, kein `.bubble` — insertOrdered, Tagestrenner, Doppel-Erkennung und Zähler sehen sie nicht.
 *
 * EIN Besitzer für die Blase (dieses Modul). Sie verschwindet, sobald
 *   - der Server „aus“ meldet (text null — Beitrag kam, Status gelöscht, Verfall am Server),
 *   - ein Beitrag des Schmieds (`.bubble.entwickler`, `data-entwickler` fehlt oder „schmied“) eingefügt wird, der nicht älter ist als das Warten (Beitrag ersetzt sie),
 *   - ihre Restdauer abläuft (`restMs`, höchstens SCHMIED_STATUS_VERFALL_MS — auch wenn nach einem Abriss kein „aus“ mehr kam),
 *   - der Strom sich neu verbindet (`verbindungNeu`): der Server liefert danach den aktuellen Stand sofort nach — ein Status, der
 *     während des Abrisses endete, bleibt so nie hängen.
 *
 * ⚠️ AM ENDE, OHNE ZU VERSCHIEBEN: Neue Blasen hängt insertOrdered HINTER sie. Statt sie dann umzuhängen (jedes Umhängen wäre im
 *    `aria-live`-Verlauf eine neue Meldung und ein Neustart der Einblendung), steht sie per CSS `order` sichtbar zuletzt
 *    (#chat ist eine Flex-Spalte). Nur wenn der Verlauf geleert wird (Abgleich, „Verlauf löschen“), hängt sie sich wieder an.
 *    Wählt die Ortsrechnung beim Nachladen sie als Anker (lib/scroll-ort.ts nimmt das erste Kind in DOM-Reihenfolge, das
 *    unter den oberen Rand reicht), stimmt der Ort trotzdem: sie bewegt sich mit dem Inhalt wie jede Blase.
 * ⚠️ SCROLLEN: kein eigener Scroll-Schreiber. Erscheint sie, ruft sie `folgen` (lib/chat-folgen.ts) — das zieht nur nach, wenn
 *    der Verlauf ohnehin am Ende klebte, und nie gegen einen Finger (lib/scroll-ruhe.ts).
 * ⚠️ Der Text kommt vom Entwickler: nur über textContent, höchstens SCHMIED_STATUS_TEXT_HOECHSTENS Zeichen.
 */
import { entwickler, type EntwicklerId } from "../../shared/entwickler.js";
import { SCHMIED_NAME } from "../../shared/schmied.js";
import {
  ANSTOSS_SPERRE_MS,
  SCHMIED_STATUS_TEXT_HOECHSTENS,
  SCHMIED_STATUS_VERFALL_MS,
  WARTE_ABGELAUFEN_MS,
  WARTE_SPAET_MS,
  WARTE_STILL_MS,
  stufenText,
  warteStufe,
  type SchmiedStatusStand,
  type WarteStufe,
} from "../../shared/schmied-status.js";
import { alsStatus, baueWarteZeile } from "./warteblase.js";

export const WARTEBLASE_KLASSE = "schmied-warteblase";

/** Ein Beitrag zählt als Antwort, wenn er höchstens so viel älter ist als der Beginn des Wartens (Uhren Gerät/Server). */
export const BEITRAG_TOLERANZ_MS = 5_000;

interface Uhr {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(h: unknown): void;
  jetzt(): number;
}

const echteUhr: Uhr = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
  jetzt: () => Date.now(),
};

/** Stößt den Entwickler an (POST /api/entwickler/anstossen); `true` = angenommen. */
export type AnstossSender = (wer: EntwicklerId) => Promise<boolean> | boolean;

export const sendeAnstoss: AnstossSender = async (wer) => {
  try {
    const r = await fetch("/api/entwickler/anstossen", {
      method: "POST",
      credentials: "include",
      headers: { "X-Vespera-Knopf": "1", "Content-Type": "application/json" },
      body: JSON.stringify({ id: wer }),
    });
    return r.ok;
  } catch {
    return false;
  }
};

/** Merkt, welche Meldung Martin entfernt hat: Text + Verfallszeitpunkt (gleicher Stand nach Neuladen = weiter weg; neue Nachricht = später). */
const ENTFERNT_SCHLUESSEL = "vespera.entwickler-meldung-entfernt.";
const ENTFERNT_TOLERANZ_MS = 3_000;
interface Entfernt {
  text: string;
  bis: number;
}
function leseEntfernt(wer: EntwicklerId): Entfernt | null {
  try {
    const roh = JSON.parse(globalThis.localStorage?.getItem(ENTFERNT_SCHLUESSEL + wer) ?? "null") as Partial<Entfernt> | null;
    return roh && typeof roh.text === "string" && typeof roh.bis === "number" ? { text: roh.text, bis: roh.bis } : null;
  } catch {
    return null;
  }
}
function schreibeEntfernt(wer: EntwicklerId, wert: Entfernt | null): void {
  try {
    if (wert) globalThis.localStorage?.setItem(ENTFERNT_SCHLUESSEL + wer, JSON.stringify(wert));
    else globalThis.localStorage?.removeItem(ENTFERNT_SCHLUESSEL + wer);
  } catch {
    /* Speicher gesperrt: die Meldung bleibt dann nur bis zum Neuladen weg */
  }
}

export interface SchmiedWarteblase {
  /** Den Stand vom Strom zeigen (text null = weg). */
  zeige(stand: SchmiedStatusStand): void;
  /** Der Strom hat sich (neu) verbunden — weg; der Server schickt einen noch lebenden Stand gleich wieder. */
  verbindungNeu(): void;
  /** Steht die Blase gerade im Verlauf? */
  sichtbar(): boolean;
  /** Hat Martin die Meldung entfernt (dieses Gerät)? Dann steht der Zustand auch nicht in der Aktivitätszeile. */
  entfernt(): boolean;
}

/** Baut die Blase (noch nicht eingehängt). Zeile und Status teilt sie mit Vesperas Warteblase (lib/warteblase.ts). */
function baueBlase(doc: Document, wer: EntwicklerId): { blase: HTMLElement; text: HTMLElement; sekunden: HTMLElement; aktionen: HTMLElement; anstoss: HTMLButtonElement; ausblenden: HTMLButtonElement } {
  const blase = doc.createElement("div");
  blase.className = WARTEBLASE_KLASSE;
  // 17.10.2026: die Blase des Zimmermanns ist dieselbe, mit seinem Namen und seinen Farben (style.css `.schmied-warteblase.e-zimmermann`).
  if (wer !== "schmied") blase.classList.add(`e-${wer}`);
  // Höflich, als Ganzes: ein Bildschirmleser sagt „Schmied bereitet eine Antwort vor“, ohne den Fokus zu nehmen.
  alsStatus(blase);
  const name = doc.createElement("span");
  name.className = "schmied-warteblase-name";
  name.textContent = wer === "schmied" ? SCHMIED_NAME : entwickler(wer).name;
  const { zeile, text } = baueWarteZeile(doc, WARTEBLASE_KLASSE);
  // Die Sekunden („· 15 s“) stehen für sich und sind für den Bildschirmleser stumm: sie ändern sich im Takt, und `aria-atomic`
  // läse sonst alle 15 s die ganze Zeile neu vor. Der Platz ist reserviert (CSS), die Blase springt nicht.
  const sekunden = doc.createElement("span");
  sekunden.className = "schmied-warteblase-sekunden";
  sekunden.setAttribute("aria-hidden", "true");
  zeile.appendChild(sekunden);
  // Stufe „still“ (shared/schmied-status.ts): zwei Knöpfe, sonst verborgen. Keine Inline-Styles — alles in style.css.
  const aktionen = doc.createElement("div");
  aktionen.className = "schmied-warteblase-aktionen";
  const knopf = (beschriftung: string): HTMLButtonElement => {
    const k = doc.createElement("button");
    k.type = "button";
    k.className = "schmied-warteblase-knopf";
    k.textContent = beschriftung;
    return k;
  };
  const anstoss = knopf("Nochmal anstoßen");
  const ausblenden = knopf("Meldung entfernen");
  ausblenden.setAttribute("aria-label", "Meldung entfernen");
  ausblenden.classList.add("schmied-warteblase-knopf-entfernen");
  aktionen.append(anstoss, ausblenden);
  blase.append(name, " ", zeile); // `aktionen` hängt erst in Stufe „still“ dran (zeigeStufe) — sonst läse der Bildschirmleser die Knöpfe mit
  return { blase, text, sekunden, aktionen, anstoss, ausblenden };
}

/** „in der Reihe · 15 s“ → Hauptteil „in der Reihe“ und Zeit „· 15 s“ (ohne Zeit: leer). */
export function teileZeit(t: string): { haupt: string; zeit: string } {
  const m = /^(.*?)( · \d+ (?:s|Min))$/.exec(t);
  return m ? { haupt: m[1], zeit: m[2].trim() } : { haupt: t, zeit: "" };
}

/** Ist `el` ein Beitrag DIESES Entwicklers (`wer`), der nicht vor dem Warten liegt? */
function istNeuerBeitrag(el: Node, seit: number, wer: EntwicklerId): boolean {
  if (!(el instanceof HTMLElement) || !el.classList.contains("bubble") || !el.classList.contains("entwickler")) return false;
  // 16.10.2026 (Zimmermann P1): nur ein Beitrag DES SCHMIEDS ersetzt sie — eine Blase des Zimmermanns (oder eines unbekannten Absenders,
  // `data-entwickler` aus lib/entwickler-blase.ts) lässt „Schmied bereitet vor“ stehen. Ohne Angabe: Schmied (Blasen vor P1).
  if ((el.dataset.entwickler ?? "schmied") !== wer) return false;
  const ts = Number(el.dataset.ts);
  // Ohne Zeitstempel (sollte es nicht geben): als neu werten — lieber weg als hängen.
  return !Number.isFinite(ts) || ts <= 0 || ts >= seit - BEITRAG_TOLERANZ_MS;
}

/**
 * Die Warteblase für den Verlauf `chat`. `folgen`: der Verlauf zieht nach, wenn er am Ende klebte (lib/chat-folgen.ts).
 */
export function erzeugeSchmiedWarteblase(
  chat: HTMLElement,
  folgen: () => void,
  uhr: Uhr = echteUhr,
  wer: EntwicklerId = "schmied",
  sendeAnstossAn: AnstossSender = sendeAnstoss,
  /** Ruft sie, wenn die Blase erscheint, verschwindet oder ein-/ausgeblendet wird — die Aktivitätszeile zeichnet sich dann sofort neu (EINE Anzeige je Entwickler). */
  beiAenderung: () => void = () => undefined,
): SchmiedWarteblase {
  const { blase, text, sekunden, aktionen, anstoss, ausblenden } = baueBlase(chat.ownerDocument, wer);
  let aktiv = false;
  let seit = 0;
  let verfall: unknown = null;
  // Stufen des Wartens: `basis` = der Text vom Server, `basisSeit` = seit wann er (unverändert) steht.
  let basis = "";
  let basisSeit = 0;
  let verfallBis = 0;
  let stufeTimer: unknown = null;
  let ausgeblendet = false;
  let anstossBis = 0;
  let anstossTimer: unknown = null;

  const beobachter =
    typeof MutationObserver === "undefined"
      ? null
      : new MutationObserver((liste) => {
          if (!aktiv) return;
          for (const a of liste) {
            for (const n of Array.from(a.addedNodes)) {
              if (istNeuerBeitrag(n, seit, wer)) {
                weg();
                return;
              }
            }
          }
          // Der Verlauf wurde geleert oder neu aufgebaut: wieder ans Ende (sichtbar zuletzt ohnehin per CSS `order`).
          if (!blase.isConnected && chat.isConnected) chat.appendChild(blase);
        });

  function weg(): void {
    aktiv = false;
    beobachter?.disconnect();
    if (verfall !== null) uhr.clearTimeout(verfall);
    verfall = null;
    if (stufeTimer !== null) uhr.clearTimeout(stufeTimer);
    stufeTimer = null;
    if (anstossTimer !== null) uhr.clearTimeout(anstossTimer);
    anstossTimer = null;
    anstoss.disabled = false;
    anstoss.textContent = "Nochmal anstoßen";
    basis = "";
    ausgeblendet = false;
    blase.hidden = false;
    aktionen.remove();
    delete blase.dataset.stufe;
    blase.remove();
    text.textContent = "";
    sekunden.textContent = "";
    beiAenderung();
  }

  /** Zeigt Text und Knöpfe der Stufe und plant den Wechsel zur nächsten. */
  function zeigeStufe(): void {
    if (stufeTimer !== null) uhr.clearTimeout(stufeTimer);
    stufeTimer = null;
    const seitMs = uhr.jetzt() - basisSeit;
    const stufe: WarteStufe = warteStufe(basis, seitMs);
    const neuerText = stufenText(basis, stufe);
    if (text.textContent !== neuerText) text.textContent = neuerText;
    blase.dataset.stufe = stufe;
    if (stufe === "still" || stufe === "abgelaufen") blase.appendChild(aktionen);
    else aktionen.remove();
    // Abgelaufen: nichts läuft mehr — keine Sekunden.
    if (stufe === "abgelaufen") sekunden.textContent = "";
    const warVerborgen = blase.hidden;
    blase.hidden = (stufe === "still" || stufe === "abgelaufen") && ausgeblendet;
    if (warVerborgen !== blase.hidden) beiAenderung();
    const naechste = stufe === "ok" ? WARTE_SPAET_MS : stufe === "spaet" ? WARTE_STILL_MS : stufe === "still" ? WARTE_ABGELAUFEN_MS : 0;
    if (naechste > 0 && warteStufe(basis, naechste) !== "ok") {
      stufeTimer = uhr.setTimeout(() => {
        stufeTimer = null;
        if (aktiv) zeigeStufe();
      }, naechste - seitMs);
    }
  }

  anstoss.addEventListener("click", (ev) => {
    if (!ev.isTrusted) return; // künstliches Ereignis (Skript, Fremdtext): ignorieren
    if (uhr.jetzt() < anstossBis) return; // höchstens einmal je Minute
    anstossBis = uhr.jetzt() + ANSTOSS_SPERRE_MS;
    anstoss.disabled = true;
    anstoss.textContent = "Angestoßen …";
    const frei = (beschriftung: string): void => {
      anstoss.textContent = beschriftung;
      if (anstossTimer !== null) uhr.clearTimeout(anstossTimer);
      anstossTimer = uhr.setTimeout(() => {
        anstossTimer = null;
        anstoss.disabled = false;
        anstoss.textContent = "Nochmal anstoßen";
      }, Math.max(anstossBis - uhr.jetzt(), 0));
    };
    void Promise.resolve(sendeAnstossAn(wer)).then(
      (ok) => frei(ok ? "Angestoßen" : "Nicht angekommen"),
      () => frei("Nicht angekommen"),
    );
  });
  ausblenden.addEventListener("click", (ev) => {
    if (!ev.isTrusted) return;
    ausgeblendet = true;
    blase.hidden = true;
    schreibeEntfernt(wer, { text: basis, bis: verfallBis });
    beiAenderung();
  });

  function zeige(stand: SchmiedStatusStand): void {
    const t = stand.text?.slice(0, SCHMIED_STATUS_TEXT_HOECHSTENS) ?? "";
    if (!t) {
      weg();
      return;
    }
    if (verfall !== null) uhr.clearTimeout(verfall);
    const rest = Math.min(Math.max(stand.restMs, 0), SCHMIED_STATUS_VERFALL_MS);
    verfall = uhr.setTimeout(() => {
      verfall = null;
      weg();
    }, rest);
    const { haupt, zeit } = teileZeit(t);
    // Gleicher Text, aber der Verfall liegt später als zuvor: der Server hat neu gesetzt (neue Nachricht) — Wartezeit von vorn.
    const verfallNeu = uhr.jetzt() + rest;
    const neuGesetzt = haupt === basis && verfallNeu > verfallBis + 2000;
    verfallBis = verfallNeu;
    if (haupt !== basis || neuGesetzt) {
      const erstesMal = basis === "";
      basis = haupt;
      basisSeit = uhr.jetzt();
      ausgeblendet = false;
      const gemerkt = leseEntfernt(wer);
      // Dieselbe Meldung (gleicher Text, gleicher Verfall) wie die, die Martin entfernt hat → bleibt weg; eine neue Nachricht holt sie zurück.
      if (gemerkt?.text === haupt && verfallNeu <= gemerkt.bis + ENTFERNT_TOLERANZ_MS) ausgeblendet = true;
      else if (gemerkt && !erstesMal) schreibeEntfernt(wer, null);
    }
    zeigeStufe();
    if (sekunden.textContent !== zeit) sekunden.textContent = zeit;
    if (aktiv && blase.isConnected) return;
    aktiv = true;
    seit = uhr.jetzt();
    chat.appendChild(blase);
    beobachter?.observe(chat, { childList: true });
    beiAenderung();
    folgen();
  }

  return {
    zeige,
    verbindungNeu: weg,
    sichtbar: () => aktiv && blase.isConnected && !blase.hidden,
    entfernt: () => aktiv && ausgeblendet && (blase.dataset.stufe === "still" || blase.dataset.stufe === "abgelaufen"),
  };
}
